// frontend/src/pages/admin/GiftRegistry.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import { useCouple } from '../../contexts/CoupleContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import RegistryService from '../../services/registry.service';
import UploadService from '../../services/upload.service';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import Alert from '../../components/common/Alert';
import Modal from '../../components/common/Modal';
import { Gift, PlusCircle, Edit2, Trash2, AlertCircle } from 'react-feather';
import { Gift as GiftType } from '../../types';

const GiftRegistry: React.FC = () => {
  const { currentCouple } = useCouple();
  
  const [gifts, setGifts] = useState<GiftType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [currentGift, setCurrentGift] = useState<GiftType | null>(null);
  
  // Track which images failed to load
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  
  // Form state
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [link, setLink] = useState<string>('');
  const [image, setImage] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [priority, setPriority] = useState<string>('3');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  
  useEffect(() => {
    if (currentCouple) {
      fetchGifts();
    }
  }, [currentCouple]);
  
  const fetchGifts = async () => {
    if (!currentCouple) return;
    
    try {
      setLoading(true);
      const fetchedGifts = await RegistryService.getCoupleGifts(currentCouple._id);
      setGifts(fetchedGifts);
      setError(null);
      
      // Reset failed images when fetching new gifts
      setFailedImages({});
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch gifts';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Get an optimized image URL for display
  const getDisplayUrl = (url: string): string => {
    if (!url) return '';
    return UploadService.getOptimizedImageUrl(url);
  };
  
  const handleImageError = (giftId: string) => {
    setFailedImages(prev => ({
      ...prev,
      [giftId]: true
    }));
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImageFile(e.target.files[0]);
    }
  };
  
  const uploadImage = async (): Promise<string> => {
    if (!imageFile) return '';
    
    try {
      setIsUploading(true);
      const imageUrl = await UploadService.uploadImage(imageFile, 'gifts');
      return imageUrl;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload image';
      setError(errorMessage);
      return '';
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleAddGift = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!currentCouple) return;
    
    try {
      let giftImageUrl = image;
      
      if (imageFile) {
        giftImageUrl = await uploadImage();
      }
      
      const giftData = {
        name,
        description,
        price: price ? Number(price) : undefined,
        link,
        image: giftImageUrl,
        category,
        priority: Number(priority),
        couple: currentCouple._id
      };
      
      await RegistryService.createGift(giftData);
      resetForm();
      setIsAddModalOpen(false);
      fetchGifts();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add gift';
      setError(errorMessage);
    }
  };
  
  const handleEditGift = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!currentCouple || !currentGift) return;
    
    try {
      let giftImageUrl = image;
      
      if (imageFile) {
        giftImageUrl = await uploadImage();
      }
      
      const giftData = {
        name,
        description,
        price: price ? Number(price) : undefined,
        link,
        image: giftImageUrl,
        category,
        priority: Number(priority),
      };
      
      await RegistryService.updateGift(currentGift._id, giftData);
      resetForm();
      setIsEditModalOpen(false);
      fetchGifts();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update gift';
      setError(errorMessage);
    }
  };
  
  const handleDeleteGift = async () => {
    if (!currentGift) return;
    
    try {
      await RegistryService.deleteGift(currentGift._id);
      setIsDeleteModalOpen(false);
      fetchGifts();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete gift';
      setError(errorMessage);
    }
  };
  
  const openEditModal = (gift: GiftType) => {
    setCurrentGift(gift);
    setName(gift.name);
    setDescription(gift.description || '');
    setPrice(gift.price?.toString() || '');
    setLink(gift.link || '');
    setImage(gift.image || '');
    setCategory(gift.category || '');
    setPriority(gift.priority?.toString() || '3');
    setIsEditModalOpen(true);
  };
  
  const openDeleteModal = (gift: GiftType) => {
    setCurrentGift(gift);
    setIsDeleteModalOpen(true);
  };
  
  const resetForm = () => {
    setName('');
    setDescription('');
    setPrice('');
    setLink('');
    setImage('');
    setCategory('');
    setPriority('3');
    setImageFile(null);
    setCurrentGift(null);
  };
  
  if (!currentCouple) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
          <p className="text-center text-gray-500">Please create a couple profile first.</p>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
        <div className="md:flex md:items-center md:justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Gift Registry</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage your wedding gift registry
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Button 
              onClick={() => {
                resetForm();
                setIsAddModalOpen(true);
              }}
              leftIcon={<PlusCircle className="h-4 w-4" />}
            >
              Add Gift
            </Button>
          </div>
        </div>
        
        {error && (
          <Alert
            type="error"
            message={error}
            dismissible
            onDismiss={() => setError(null)}
            className="mb-6"
          />
        )}
        
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading gifts...</p>
          </div>
        ) : gifts.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <Gift className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">No gifts added yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Start adding gifts to your registry for your guests to view.
            </p>
            <div className="mt-6">
              <Button 
                onClick={() => {
                  resetForm();
                  setIsAddModalOpen(true);
                }}
              >
                Add Your First Gift
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {gifts.map((gift) => (
              <div key={gift._id} className="bg-white shadow rounded-lg overflow-hidden">
                <div className="relative h-48 bg-gray-200 flex items-center justify-center">
                  {gift.image ? (
                    failedImages[gift._id] ? (
                      <div className="h-full w-full flex items-center justify-center">
                        <div className="text-center p-4">
                          <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">Image could not be loaded</p>
                        </div>
                      </div>
                    ) : (
                      <img 
                        src={getDisplayUrl(gift.image)} 
                        alt={gift.name} 
                        className="h-full w-full object-cover"
                        onError={() => handleImageError(gift._id)}
                        loading="lazy"
                      />
                    )
                  ) : (
                    <Gift className="h-12 w-12 text-gray-400" />
                  )}
                </div>
                <div className="p-4">
                  <div className="flex justify-between">
                    <h3 className="text-lg font-medium text-gray-900">{gift.name}</h3>
                    <span 
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${gift.status === 'purchased' 
                          ? 'bg-green-100 text-green-800' 
                          : gift.status === 'reserved' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-blue-100 text-blue-800'
                        }`}
                    >
                      {gift.status === 'purchased' 
                        ? 'Purchased' 
                        : gift.status === 'reserved' 
                        ? 'Reserved' 
                        : 'Available'
                      }
                    </span>
                  </div>
                  {gift.price && (
                    <p className="mt-1 text-sm text-gray-600">${gift.price.toFixed(2)}</p>
                  )}
                  {gift.description && (
                    <p className="mt-2 text-sm text-gray-500 line-clamp-2">{gift.description}</p>
                  )}
                  <div className="mt-4 flex justify-end space-x-2">
                    <button
                      onClick={() => openEditModal(gift)}
                      className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(gift)}
                      className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Add Gift Modal */}
        <Modal
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          title="Add Gift"
        >
          <form onSubmit={handleAddGift} className="space-y-4">
            <Input
              id="gift-name"
              label="Gift Name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            
            <Textarea
              id="gift-description"
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                id="gift-price"
                label="Price"
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
              
              <Input
                id="gift-category"
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Kitchen, Bedroom"
              />
            </div>
            
            <Input
              id="gift-link"
              label="Link to Purchase"
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://..."
            />
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Image</label>
              <div className="mt-1 flex items-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsAddModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isUploading}
                disabled={isUploading}
              >
                Add Gift
              </Button>
            </div>
          </form>
        </Modal>
        
        {/* Edit Gift Modal */}
        <Modal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          title="Edit Gift"
        >
          <form onSubmit={handleEditGift} className="space-y-4">
            <Input
              id="edit-gift-name"
              label="Gift Name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            
            <Textarea
              id="edit-gift-description"
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                id="edit-gift-price"
                label="Price"
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
              
              <Input
                id="edit-gift-category"
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Kitchen, Bedroom"
              />
            </div>
            
            <Input
              id="edit-gift-link"
              label="Link to Purchase"
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://..."
            />
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Image</label>
              {image && (
                <div className="mt-1 mb-2">
                  {failedImages[currentGift?._id || ''] ? (
                    <div className="h-24 w-full bg-gray-100 flex items-center justify-center rounded">
                      <div className="text-center p-2">
                        <AlertCircle className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-500">Image could not be loaded</p>
                      </div>
                    </div>
                  ) : (
                    <img 
                      src={getDisplayUrl(image)} 
                      alt={name} 
                      className="h-24 w-auto object-cover rounded"
                      onError={() => currentGift && handleImageError(currentGift._id)}
                    />
                  )}
                </div>
              )}
              <div className="mt-1 flex items-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isUploading}
                disabled={isUploading}
              >
                Save Changes
              </Button>
            </div>
          </form>
        </Modal>
        
        {/* Delete Confirmation Modal */}
        <Modal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Delete Gift"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Are you sure you want to delete "{currentGift?.name}"? This action cannot be undone.
            </p>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteGift}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default GiftRegistry;